import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { AppFrame } from '../components/AppNav';
import { Banner, Field, Loading } from '../components/ui';
import { formatDateTime } from '../lib/format';
import { useSession } from '../state/session';
import { useToast } from '../state/toast';
import { useMfaGuard } from '../components/MfaGuard';

export function Developers() {
  const { orgId } = useSession();
  const { notify } = useToast();
  const { guard, challenge } = useMfaGuard();
  const [keys, setKeys] = useState<any>(null);
  const [hooks, setHooks] = useState<any>(null);
  const [sealing, setSealing] = useState<any>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [hookUrl, setHookUrl] = useState('');
  const [hookSecret, setHookSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    const [k, w, s] = await Promise.all([
      api.get(`/organizations/${orgId}/api-keys`).catch(() => null),
      api.get(`/organizations/${orgId}/webhooks`).catch(() => null),
      api.get(`/organizations/${orgId}/sealing`).catch(() => null),
    ]);
    setKeys(k); setHooks(w); setSealing(s);
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  const createKey = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        const result = await api.post(`/organizations/${orgId}/api-keys`,
                                      { name: newKeyName });
        setNewSecret(result.secret);
        setNewKeyName('');
        await load();
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'The key could not be created.');
    } finally { setBusy(false); }
  };

  const revoke = async (keyId: string) => {
    await guard(async () => {
      await api.del(`/organizations/${orgId}/api-keys/${keyId}`);
      notify('API key revoked.', 'ok');
      await load();
    });
  };

  const createHook = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        const result = await api.post(`/organizations/${orgId}/webhooks`,
                                      { url: hookUrl });
        setHookSecret(result.secret);
        setHookUrl('');
        await load();
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'The endpoint could not be created.');
    } finally { setBusy(false); }
  };

  if (!keys) return <AppFrame><div className="shell section"><Loading /></div></AppFrame>;

  const apiAllowed = keys.entitlement?.api_access;

  return (
    <AppFrame>
      {challenge}
      <div className="shell section stack-l">
        <header>
          <h1 style={{ margin: 0 }}>Developers</h1>
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            The same API and security boundaries first-party integrations use.
          </p>
        </header>

        {error && <Banner tone="danger" title="That did not work">{error}</Banner>}

        {!apiAllowed && (
          <Banner tone="warn" title="API access is a Pro feature">
            Your current plan is {keys.entitlement?.plan_name}. API and dashboard
            sends share one monthly allowance — API access does not add quota and
            cannot be used to exceed the cap.{' '}
            <Link to="/app/billing">See plans</Link>.
          </Banner>
        )}

        <div className="card stack">
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>API keys</h2>
          {newSecret && (
            <Banner tone="ok" title="Copy this key now — it is not shown again">
              <pre className="codebox" style={{ marginTop: 8 }}>{newSecret}</pre>
            </Banner>
          )}
          <form className="row" onSubmit={createKey}>
            <div style={{ flex: '1 1 220px' }}>
              <Field label="Key name" id="key-name">
                <input id="key-name" className="input" value={newKeyName}
                       placeholder="Production integration"
                       onChange={(event) => setNewKeyName(event.target.value)} />
              </Field>
            </div>
            <button className="btn btn--primary" disabled={busy || !apiAllowed}
                    style={{ alignSelf: 'end' }}>
              Create key
            </button>
          </form>

          {keys.keys.length === 0 ? (
            <p className="muted small" style={{ margin: 0 }}>No API keys yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Name</th><th>Prefix</th><th>Created</th><th>Last used</th><th></th></tr>
                </thead>
                <tbody>
                  {keys.keys.map((key: any) => (
                    <tr key={key.id} style={{ opacity: key.revoked_at ? 0.5 : 1 }}>
                      <td>{key.name}</td>
                      <td><span className="digest">{key.prefix}…</span></td>
                      <td className="nowrap">{formatDateTime(key.created_at)}</td>
                      <td className="nowrap">{formatDateTime(key.last_used_at)}</td>
                      <td>
                        {key.revoked_at ? <span className="pill">Revoked</span> : (
                          <button className="btn btn--small btn--danger"
                                  onClick={() => void revoke(key.id)}>Revoke</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card stack">
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Webhooks</h2>
          <p className="small muted" style={{ margin: 0 }}>
            Delivery is at least once with retry and backoff. Deduplicate on the
            event id and reconcile through the authoritative GET endpoints — events
            can arrive out of order, and an out-of-order event never regresses
            status. Payloads carry ids and hashes, never document contents.
          </p>
          {hookSecret && (
            <Banner tone="ok" title="Signing secret — copy it now">
              <pre className="codebox" style={{ marginTop: 8 }}>{hookSecret}</pre>
              Signature header: <code>x-esign-signature: v1=HMAC-SHA256(secret,
              "&lt;x-esign-timestamp&gt;.&lt;raw body&gt;")</code>
            </Banner>
          )}
          <form className="row" onSubmit={createHook}>
            <div style={{ flex: '1 1 260px' }}>
              <Field label="Endpoint URL" id="hook-url" hint="HTTPS only.">
                <input id="hook-url" className="input" type="url" value={hookUrl}
                       placeholder="https://example.com/hooks/esign"
                       onChange={(event) => setHookUrl(event.target.value)} />
              </Field>
            </div>
            <button className="btn btn--primary" disabled={busy || !hookUrl}
                    style={{ alignSelf: 'end' }}>
              Add endpoint
            </button>
          </form>

          {hooks?.endpoints?.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>URL</th><th>Events</th><th>Status</th></tr></thead>
                <tbody>
                  {hooks.endpoints.map((endpoint: any) => (
                    <tr key={endpoint.id}>
                      <td className="truncate" style={{ maxWidth: 280 }}>{endpoint.url}</td>
                      <td>{endpoint.events.length} subscribed</td>
                      <td>
                        <span className={`pill pill--${endpoint.active ? 'ok' : ''}`}>
                          {endpoint.active ? 'Active' : 'Paused'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hooks?.deliveries?.length > 0 && (
            <>
              <h3 className="card__title" style={{ marginTop: 8 }}>Recent deliveries</h3>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr><th>Event</th><th>Attempts</th><th>Status</th><th>When</th></tr>
                  </thead>
                  <tbody>
                    {hooks.deliveries.slice(0, 15).map((delivery: any) => (
                      <tr key={delivery.id}>
                        <td>{delivery.event_type}</td>
                        <td>{delivery.attempts}</td>
                        <td>
                          <span className={`pill pill--${delivery.delivered_at ? 'ok' : 'warn'}`}>
                            {delivery.delivered_at ? 'Delivered' : `Retrying${
                              delivery.last_status ? ` (${delivery.last_status})` : ''}`}
                          </span>
                        </td>
                        <td className="nowrap">{formatDateTime(delivery.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="card stack-s">
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Quick start</h2>
          <pre className="codebox">{`# Create and send an agreement in one call
curl -X POST https://your-host/api/v1/envelopes \\
  -H "Authorization: Bearer $ESIGNLOOP_API_KEY" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "content-type: application/json" \\
  -d '{
    "title": "Service agreement",
    "document_id": "doc_...",
    "routing": "sequential",
    "recipients": [
      {"name": "Dana Whitfield", "email": "dana@example.com", "role": "signer"}
    ],
    "fields": [
      {"kind": "signature", "page": 0, "x": 72, "y": 200,
       "width": 190, "height": 46, "label": "Signature"}
    ],
    "source_reference": {"crm": "trusso", "job_id": "job_1842"},
    "send": true
  }'

# Reconcile after a webhook outage — this GET is authoritative
curl https://your-host/api/v1/envelopes/env_... \\
  -H "Authorization: Bearer $ESIGNLOOP_API_KEY"`}</pre>
        </div>

        {sealing && (
          <div className="card stack-s">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Document sealing</h2>
            <div className="kv">
              <div className="kv__k">Assurance profile</div>
              <div className="kv__v"><span className="digest">{sealing.profile}</span></div>
              <div className="kv__k">Phase 2 sealing</div>
              <div className="kv__v">
                <span className={`pill${sealing.phase2_enabled ? ' pill--ok' : ''}`}>
                  {sealing.phase2_enabled ? 'Enabled' : 'Not enabled'}
                </span>
              </div>
              <div className="kv__k">Queue</div>
              <div className="kv__v">
                {sealing.queue.queued} queued · {sealing.queue.leased} in progress ·{' '}
                {sealing.queue.accepted} sealed · {sealing.queue.failed} failed
              </div>
            </div>
            <p className="small muted" style={{ margin: 0 }}>{sealing.note}</p>
          </div>
        )}
      </div>
    </AppFrame>
  );
}
